import { useRecoilValue } from 'recoil';
import {
    hasUnsavedChangesState,
    patientRepositoryState,
} from '~/state/atoms/patientAtoms';
import AppConfig from '~/utils/appconfig';

export type PatientGuardResult =
    | { action: 'allow' }
    | {
          action: 'conflict';
          loadedPatientId: string;
          incomingPatientIds: string[];
          hasUnsavedChanges: boolean;
          clinicalUse: boolean;
      };

/**
 * Hook that provides a guard function to check whether incoming DICOM data
 * belongs to a different patient than the one currently loaded.
 *
 * A conflict is always raised when the incoming data belongs to a different
 * patient. The {@link clinicalUse} flag on the result lets callers decide
 * whether to offer a "Load Both" dismiss option (non-clinical) or restrict
 * the user to discard/switch only (clinical).
 */
export default function usePatientGuard() {
    const patientRepository = useRecoilValue(patientRepositoryState);
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);

    function checkPatientConflict(
        incomingPatientIds: string[]
    ): PatientGuardResult {
        const loadedPatients = patientRepository.getAll();
        if (loadedPatients.length === 0) {
            return { action: 'allow' };
        }

        const loadedPatientIds = new Set(
            loadedPatients.map((p) => p.dataSet.PatientID)
        );

        const conflictingIds = incomingPatientIds.filter(
            (id) => !loadedPatientIds.has(id)
        );

        if (conflictingIds.length === 0) {
            return { action: 'allow' };
        }

        return {
            action: 'conflict',
            loadedPatientId: loadedPatients
                .map((p) => p.dataSet.PatientID)
                .join(', '),
            incomingPatientIds: conflictingIds,
            hasUnsavedChanges,
            clinicalUse: AppConfig.clinicalUse,
        };
    }

    return { checkPatientConflict };
}
